import { useMutation } from '@tanstack/react-query';
import { deliveryProblem, knownNotSent } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';

/**
 * "Send a test to me" (MAP 249 spec §9, §11): one email in fixed words to the owner's OWN
 * sign-up address, on ServWell's own mail (`integrations.testEmail`; three a person a day). It
 * moved here from Connected services with ticket 07.
 *
 * A key being present is not a key that works: sending a real one is the only way to know. It
 * proves ServWell's own mail reaches the owner, and only that (customer emails leave by the
 * shop-mail sender, configured apart: the answer never speaks for them). The reason is what the
 * owner came for, so a failure says it (`deliveryProblem`), and the next move in THIS card's
 * voice: not
 * `deliveryFix`, whose `rejected` arm says "send yourself a test", which under this button is a
 * loop (`delivery-copy.test.ts` pins both halves).
 */
export function SendTestEmail() {
  const trpc = useTRPC();
  const test = useMutation(trpc.integrations.testEmail.mutationOptions());
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Button
        variant="ghost"
        aria-disabled={test.isPending || undefined}
        onClick={() => {
          if (!test.isPending) test.mutate();
        }}
      >
        {test.isPending ? 'Sending…' : 'Send a test to me'}
      </Button>
      {test.error ? (
        // A refusal in the server's words (not confirmed yet, three a day, the ceiling).
        <Notice tone="danger" alert>
          {test.error.message}
        </Notice>
      ) : test.data ? (
        test.data.delivered ? (
          <span role="status" className="text-sm text-muted-foreground">
            Sent to {test.data.to}. If it doesn&apos;t arrive, check your spam folder.
          </span>
        ) : (
          <span
            role="status"
            className="text-sm font-medium text-[var(--sw-danger-text)]"
          >
            {deliveryProblem(test.data.failure)}
            {/* ⚠️ "may or may not have arrived" for an `unreachable`: it left, and its answer
                never came back. Only a KNOWN not-sent may say it did not go. */}
            {knownNotSent(test.data.failure)
              ? ', so it didn’t send.'
              : ', so it may or may not have arrived.'}
            {/* ⚠️ Nothing about the shop's customer emails, ever (Codex 07-r1 #3): the test goes
                on ServWell's OWN mail (`EMAIL_FROM`) and customer emails on shop mail
                (`EMAIL_FROM_SHOPS`), configured apart, so one test's failure says nothing about
                them. It says what became of THIS email, and which address it went from. */}
            {' The test goes from ServWell’s own address.'}
            {test.data.failure === 'rejected'
              ? ' Contact ServWell: the mail service turned it down.'
              : test.data.failure === 'throttled'
                ? ' Try again in a few minutes.'
                : test.data.failure === 'unreachable'
                  ? ' Try it again — if it keeps happening, the problem is on their end, not yours.'
                  : ''}
          </span>
        )
      ) : null}
    </div>
  );
}
